import React, { useState } from 'react';
import { X, CircleDollarSign, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axiosInstance';
import { useApp } from '../../contexts/AppContext';
import { toast } from '../toast';
import InlinePaymentPicker from './InlinePaymentPicker';

const PRESETS = [5, 10, 25, 50, 100];

const CallTipModal = ({ isOpen, onClose, callId, recipient }) => {
  const { t } = useTranslation('livestream');
  const { walletBalance, updateBalance } = useApp();
  const [amount, setAmount] = useState(10);
  const [message, setMessage] = useState('');
  const [payment, setPayment] = useState(null); // { gateway, paymentMethodId }
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    const value = parseFloat(amount);
    if (!value || value < 1) {
      toast.error(t('videoCall.tipModal.minimumTip'));
      return;
    }
    if (!payment?.gateway) {
      toast.error(t('videoCall.common.pickPaymentMethod'));
      return;
    }
    if (payment.gateway === 'wallet' && walletBalance < value) {
      toast.error(t('videoCall.common.insufficientBalance'));
      return;
    }

    setSending(true);
    try {
      const res = await axiosInstance.post(`/calls/${callId}/tip`, {
        amount: value,
        message: message || null,
        gateway: payment.gateway,
        payment_method_id: payment.paymentMethodId,
      });
      if (res.data.success) {
        // Only wallet deducts the sender's balance locally; card charges don't
        if (payment.gateway === 'wallet') updateBalance(-value);
        toast.success(t('videoCall.tipModal.sentTip', { amount: value.toFixed(2) }));
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('videoCall.tipModal.tipFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        <div
          className="relative px-5 py-4 text-white"
          style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
        >
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <CircleDollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs opacity-80">{t('videoCall.tipModal.title')}</p>
              <p className="font-semibold truncate">{recipient?.display_name || recipient?.username}</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setAmount(p)}
                className={`flex-1 min-w-[60px] py-2 rounded-lg text-sm font-semibold transition ${
                  Number(amount) === p
                    ? 'text-white shadow'
                    : 'bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-200'
                }`}
                style={Number(amount) === p ? { backgroundColor: 'var(--color-primary-500)' } : {}}
              >
                ${p}
              </button>
            ))}
          </div>

          <div className="relative">
            <CircleDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="number"
              min="1"
              max="1000"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('videoCall.tipModal.customAmount')}
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[var(--color-primary-500)]"
            />
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder={t('videoCall.tipModal.messagePlaceholder')}
            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[var(--color-primary-500)] resize-none"
          />

          <div>
            <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('videoCall.common.payWith')}</label>
            <InlinePaymentPicker value={payment} onChange={setPayment} />
          </div>

          <button
            onClick={handleSend}
            disabled={sending || parseFloat(amount) < 1 || !payment?.gateway}
            className="w-full py-3 rounded-full text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))' }}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleDollarSign className="w-4 h-4" />}
            {t('videoCall.tipModal.sendButton', { amount: parseFloat(amount || 0).toFixed(2) })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallTipModal;
